import React, { useCallback, useEffect, useMemo } from 'react';
import { Filters } from '@10d/tend-ui-filters';
import { Button } from '@10d/tend-ui/primitives';
import { Close } from '@10d/tend-ui/icons';
import dayjs from 'dayjs';
import { useHistory } from 'react-router-dom';
import { useSearchParams, useStoredFilterValues, useFilterParams } from 'hooks';

import { FilterBlockProps } from './types';
import { WrapperStyled } from './FilterBlock.styled';

const RANGE_DATE_FORMAT = 'DD-MM-YYYY';

/**
 * Контейнер над `@10d/tend-ui-filters`, синхронизирующий значения фильтров с URL
 * и localStorage, с опциональной кнопкой сброса.
 *
 * @param value - текущее значение фильтров
 * @param filters - конфигурация фильтров
 * @param isShowClearButton - показывать ли кнопку «Сбросить все»
 * @param applySavedParams - применять ли сохранённые параметры из sessionStorage при монтировании
 * @param resetPagination - сбрасывать ли пагинацию до первой страницы при изменении фильтров
 */
export const FilterBlock = ({
  value,
  filters,
  isShowClearButton = false,
  applySavedParams,
  resetPagination,
}: FilterBlockProps) => {
  const {
    replace,
    location: { pathname },
  } = useHistory();
  const { setParams, removeAllParams } = useSearchParams<string>();
  const { getSavedParams } = useFilterParams(pathname);
  const { getFilterValuesFromStorage, setFilterValuesToStorage } = useStoredFilterValues();

  useEffect(() => {
    if (!applySavedParams) {
      return;
    }

    const params = getSavedParams();

    if (!params) {
      return;
    }

    replace({ search: params });
  }, [applySavedParams, getSavedParams, replace]);

  const handleChange = useCallback(
    (values: Record<string, unknown>) => {
      const preparedParams: Record<string, unknown> = Object.fromEntries(
        Object.entries(values).flatMap(([key, value]) => {
          if (key === 'period') {
            const [start, end] = Array.isArray(value) ? value : [];

            return [
              ['start', start ? [dayjs(start).format(RANGE_DATE_FORMAT)] : undefined],
              ['end', end ? [dayjs(end).format(RANGE_DATE_FORMAT)] : undefined],
              ['period', undefined],
            ];
          }

          const preparedValue = Array.isArray(value) ? value : [value];

          return [[key, preparedValue]];
        }),
      );

      if (resetPagination) {
        preparedParams.page = 1;
      }

      setParams(preparedParams);
    },
    [resetPagination, setParams],
  );

  const listIds = useMemo(() => filters.map(({ id }) => id), [filters]);

  return (
    <WrapperStyled>
      <Filters.Root<Record<string, unknown>>
        value={getFilterValuesFromStorage(value)}
        filters={filters}
        onFilterValuesChange={(changed, values) => {
          setFilterValuesToStorage(changed);
          handleChange(values);
        }}
      >
        <Filters.CollapseGroup defaultOpen={listIds}>
          <Filters.Form>
            {filters.map(filter => (
              <Filters.Filter key={filter.key} filter={filter} />
            ))}
            {isShowClearButton && (
              <Button
                key={'filter-clear-all'}
                before={<Close />}
                variant={'link'}
                onClick={removeAllParams}
              >
                Сбросить все
              </Button>
            )}
          </Filters.Form>
        </Filters.CollapseGroup>
      </Filters.Root>
    </WrapperStyled>
  );
};
